
import { formatDateForInput, parseDateFromInput, isValidDateFormat } from './dateUtils';

/**
 * Types for date change handlers to standardize function signatures
 */
export type DateChangeHandler = (fieldName: string) => void;

/**
 * Creates a no-operation date change handler with optional debug message
 */
export const createNoOpDateChangeHandler = (debugMessage: string = "Date change suppressed"): DateChangeHandler => {
  return (fieldName: string) => {
    console.log(`${debugMessage} for field: ${fieldName}`);
  };
};

/**
 * Check if a handler is a no-op function
 */
export const isNoOpDateChangeHandler = (handler: DateChangeHandler | undefined): boolean => {
  if (!handler) return true;
  
  const handlerString = handler.toString();
  return (
    handlerString === (() => {}).toString() ||
    handlerString.includes("Date change suppressed") ||
    handlerString.includes("Date change blocked") ||
    handlerString.includes("suppressed intentionally") ||
    handlerString === "() => {}" ||
    handlerString === "function() {}" ||
    handlerString === "function () {}"
  );
};

/**
 * Safely format a date for display or return empty string if invalid
 */
export const safeFormatDate = (date: Date | null | undefined): string => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }
  return formatDateForInput(date);
};

/**
 * Ensures we have two valid dates, with logic for swapping if end is before start
 */
export const ensureValidDateRange = (
  startDate: Date | null, 
  endDate: Date | null
): { startDate: Date, endDate: Date } => {
  const today = new Date();
  const validStartDate = startDate instanceof Date && !isNaN(startDate.getTime()) 
    ? startDate 
    : today;
    
  const validEndDate = endDate instanceof Date && !isNaN(endDate.getTime()) 
    ? endDate 
    : today;
  
  // If end date is before start date, swap them
  if (validEndDate < validStartDate) {
    return {
      startDate: validEndDate,
      endDate: validStartDate
    };
  }
  
  return {
    startDate: validStartDate,
    endDate: validEndDate
  };
};

/**
 * Creates a unique timestamp for tracking updates
 */
export const createTimestamp = (): number => Date.now();
